import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import type { ReplacementCharacterItem } from "../types/api-types";
import { getCharFromCode } from "./utils";
import ExportCsvButton from "../../shared/export/export-csv-button";
import type { CsvColumn } from "../../shared/export/csv-export";

const exportColumns: CsvColumn<ReplacementCharacterItem>[] = [
  { header: "Value", value: (item) => getCharFromCode(item.asciiValue) },
  { header: "Value ASCII Code", value: (item) => item.asciiValue },
  {
    header: "Replacement Value",
    value: (item) => getCharFromCode(item.replacementValue),
  },
  { header: "Replacement ASCII Code", value: (item) => item.replacementValue },
];

interface ReplacementCharactersTableProps {
  characters: ReplacementCharacterItem[];
  onEdit: (index: number, item: ReplacementCharacterItem) => void;
  onDelete: (index: number) => void;
}

const ReplacementCharactersTable = ({
  characters,
  onEdit,
  onDelete,
}: ReplacementCharactersTableProps) => {
  if (characters.length === 0) {
    return (
      <Box
        sx={{
          p: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary" textAlign="center">
          No replacement characters configured
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer
      sx={{
        maxHeight: 300,
        overflowY: "auto",
        "&::-webkit-scrollbar": {
          display: "none",
        },
        scrollbarWidth: "none",
        msOverflowStyle: "none",
      }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow
            sx={{
              "& .MuiTableCell-root": {
                fontWeight: 600,
                color: "primary.main",
                bgcolor: "grey.50",
              },
            }}
          >
            <TableCell>Value</TableCell>
            <TableCell>Replacement Value</TableCell>
            <TableCell align="right">
              <ExportCsvButton
                rows={characters}
                columns={exportColumns}
                fileName="replacement-characters"
              />
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {characters.map((item, index) => (
            <TableRow
              key={`replacement-char-${item.id}`}
              sx={{
                "&:hover": {
                  bgcolor: "action.hover",
                },
                "&:last-child td": {
                  borderBottom: 0,
                },
              }}
            >
              <TableCell sx={{ py: 1.5 }}>
                {getCharFromCode(item.asciiValue)}
              </TableCell>
              <TableCell sx={{ py: 1.5 }}>
                {getCharFromCode(item.replacementValue)}
              </TableCell>
              <TableCell align="right" sx={{ py: 1 }}>
                <IconButton
                  size="small"
                  onClick={() => onEdit(index, item)}
                  sx={{
                    color: "text.secondary",
                    mr: 0.5,
                    "&:hover": {
                      color: "primary.main",
                      bgcolor: "primary.lighter",
                      transform: "scale(1.1)",
                    },
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => onDelete(index)}
                  sx={{
                    color: "text.secondary",
                    "&:hover": {
                      color: "error.main",
                      bgcolor: "error.lighter",
                      transform: "scale(1.1)",
                    },
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ReplacementCharactersTable;
